import React, { useEffect, useState } from "react";
import { Button, Space, Table, Modal, Pagination, Typography } from "antd";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { SearchOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/lib/table";
import { InputSearchWithFilters } from "../../components/InputSearchWithFilters";
import styles from "./user.module.css";
import {
  GetUsers_getUsers,
  GetUsers_getUsers_schools,
} from "./../../__generated__/gql-types/GetUsers";
import { Roles } from "../../__generated__/gql-types/globalTypes";
import { useUsersWithSearch } from "../../hooks/useUsersWithSearch";
import { useMonitoring } from "../../hooks/useMonitoring";
import { USER_AUTH } from "../../store/selectors";

const { Text } = Typography;

const UsersWithSearch: React.FC = () => {
  const { 
    deleteUser, 
    loading: isLoading, 
    users,
    totalCount,
    currentPage,
    searchUsers,
    loadInitialUsers
  } = useUsersWithSearch();
  const monitor = useMonitoring();
  const user = useSelector(USER_AUTH);

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [idUser, setIdUser] = useState<string>("");
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string | undefined>();
  const [currentRole, setCurrentRole] = useState<Roles | undefined>();
  const [pageSize] = useState(10);

  const showModal = (id: string) => {
    setIsModalVisible(true);
    setIdUser(id);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleOk = async () => {
    monitor.captureMessage("Delete user", "info", { idUser, userEmail: user?.email });
    await deleteUser(idUser);
    setIsModalVisible(false);
  };

  const handleSearch = async (searchTerm?: string, role?: Roles) => {
    setCurrentSearchTerm(searchTerm);
    setCurrentRole(role);
    await searchUsers(searchTerm, role, 1, pageSize);
  };

  const handlePageChange = async (page: number) => {
    await searchUsers(currentSearchTerm, currentRole, page, pageSize);
  };

  useEffect(() => {
    loadInitialUsers();
  }, [loadInitialUsers]);

  const columns: ColumnsType<GetUsers_getUsers> = [
    {
      align: "center",
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      align: "center",
      title: "Nombre",
      dataIndex: "first_name",
      key: "firstname",
      width: 120,
    },
    {
      align: "center",
      title: "Apellido",
      dataIndex: "last_name",
      key: "lastname",
      width: 120,
    },
    {
      align: "center",
      title: "Rol",
      dataIndex: "role",
      key: "role",
      width: 120,
      render: (text) => {
        switch (text) {
          case Roles.teacher:
            return "Profesor";
          case Roles.school:
            return "Escuela";
          case Roles.admin:
            return "Admin";
          case Roles.coordinator:
            return "Coordinador";
          default:
            return "Alumno";
        }
      },
    },
    {
      align: "center",
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 200,
    },
    {
      align: "center",
      title: "Fecha Creación",
      dataIndex: "created_at",
      key: "created_at",
      width: 120,
      render: (text) => text?.split("T")[0] || "N/A",
    },
    {
      align: "center",
      title: "Escuelas",
      dataIndex: "schools",
      key: "schools",
      width: 150,
      render: (schools: GetUsers_getUsers_schools[]) => {
        return schools?.map((school: GetUsers_getUsers_schools, index: number) => {
          return (
            <div className={styles.textDraw} key={index}>
              • {school.name}
            </div>
          );
        });
      },
    },
    {
      align: "center",
      title: "Acciones",
      key: "action",
      width: 180,
      render: (_, record) => {
        return (
          <Space size={15} style={{ fontSize: 19 }}>
            <Link to={`/users/${record.id}`} state={record.id}>
              <Button icon={<SearchOutlined />} size="small">
                Editar
              </Button>
            </Link>
            <Button
              onClick={() => {
                showModal(record.id);
              }}
              danger
              size="small"
            >
              Eliminar
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div className={styles.container}>
      <h1>Usuarios</h1>
      
      <div className={styles.userContent}>
        <InputSearchWithFilters
          onSearch={handleSearch}
          loading={isLoading}
          placeholder="Buscar por nombre, apellido, email o ID"
        />
        <Button type="primary" danger>
          <Link to="/users/new">Nuevo Usuario</Link>
        </Button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">
          {totalCount > 0 ? (
            <>
              Mostrando {users.length} de {totalCount} usuarios
              {currentSearchTerm && ` (búsqueda: "${currentSearchTerm}")`}
              {currentRole && ` (rol: ${currentRole})`}
            </>
          ) : (
            "No se encontraron usuarios"
          )}
        </Text>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        loading={isLoading}
        rowKey={(record) => record.id}
        pagination={false}
        scroll={{ x: 1200 }}
      />

      {totalCount > pageSize && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Pagination
            current={currentPage}
            total={totalCount}
            pageSize={pageSize}
            onChange={handlePageChange}
            showSizeChanger={false}
            showQuickJumper
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} de ${total} usuarios`
            }
          />
        </div>
      )}

      <Modal
        title="Eliminar Usuario"
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Eliminar"
        cancelText="Cancelar"
        okButtonProps={{ danger: true }}
      >
        <p>¿Está seguro que desea eliminar este usuario?</p>
        <p><strong>Esta acción no se puede deshacer.</strong></p>
      </Modal>
    </div>
  );
};

export default UsersWithSearch;
